import {
    Ambiance,
    BoardLink,
    BoardNote,
    BoardState,
    BoardZone,
    LinkWeight,
    ClothesStyle,
    ComplexityLevel,
    DEFAULT_FIGURE,
    emptyBoardState,
    FaceExpression,
    FigureAction,
    FigureOwnerType,
    Puppet,
    Vector3,
} from "../domain/types";

export type BoardAction =
    | { type: "setState"; state: BoardState }
    | { type: "addPuppet"; puppet: Puppet }
    | { type: "movePuppet"; id: string; position: Vector3 }
    | { type: "rotatePuppet"; id: string; rotationY: number }
    | { type: "renamePuppet"; id: string; name: string }
    | { type: "setNote"; id: string; note: string }
    | { type: "setAction"; id: string; action: FigureAction }
    | {
          type: "updatePuppetAttributes";
          id: string;
          face?: FaceExpression;
          complexity?: ComplexityLevel;
          clothes?: ClothesStyle;
      }
    | { type: "removePuppet"; id: string }
    | { type: "setTitle"; title: string }
    | { type: "addNote"; note: BoardNote }
    | { type: "moveNote"; id: string; position: Vector3 }
    | { type: "editNote"; id: string; text: string }
    | { type: "removeNote"; id: string }
    | { type: "addZone"; zone: BoardZone }
    | { type: "moveZone"; id: string; position: Vector3 }
    | {
          type: "editZone";
          id: string;
          label?: string;
          color?: string;
          radius?: number;
          width?: number;
          depth?: number;
          shape?: "circle" | "square";
      }
    | { type: "removeZone"; id: string }
    | { type: "setTableShape"; shape: "square" | "circle" }
    | { type: "setTableColor"; color: string }
    | { type: "setAmbiance"; ambiance: Ambiance }
    | { type: "addLink"; link: BoardLink }
    | {
          type: "editLink";
          id: string;
          label?: string;
          color?: string;
          weight?: LinkWeight;
      }
    | { type: "removeLink"; id: string };

let _counter = 0;

/** Unique puppet id. Uses crypto.randomUUID when available so ids never
 *  collide with server-persisted puppets across reloads (a counter would
 *  restart at 1 and clash). Falls back to a counter where unavailable. */
export function nextPuppetId(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
    _counter += 1;
    return `puppet-${_counter}`;
}

export function createPuppet(name: string, overrides: Partial<Puppet> = {}): Puppet {
    return {
        id: overrides.id ?? nextPuppetId(),
        name,
        figure: overrides.figure ?? DEFAULT_FIGURE,
        face: overrides.face ?? FaceExpression.NEUTRAL,
        complexity: overrides.complexity ?? ComplexityLevel.SIMPLE,
        clothes: overrides.clothes ?? ClothesStyle.CASUAL,
        note: overrides.note ?? "",
        action: overrides.action ?? FigureAction.STAND,
        position: overrides.position ?? { x: 0, y: 0, z: 0 },
        rotationY: overrides.rotationY ?? 0,
        ...(overrides.avatar ? { avatar: overrides.avatar } : {}),
        ...(overrides.height != null ? { height: overrides.height } : {}),
        ...(overrides.ownerId !== undefined ? { ownerId: overrides.ownerId } : {}),
        ...(overrides.ownerType !== undefined ? { ownerType: overrides.ownerType } : {}),
        ...(overrides.ownerName !== undefined ? { ownerName: overrides.ownerName } : {}),
    };
}

export function createNote(text: string, position: Vector3 = { x: 0, y: 0, z: 0 }): BoardNote {
    return { id: nextPuppetId(), text, position };
}

export function createLink(source: string, target: string): BoardLink {
    return { id: nextPuppetId(), source, target, color: "#334155", weight: "medium", label: "" };
}

export function createZone(label: string, overrides: Partial<BoardZone> = {}): BoardZone {
    return {
        id: overrides.id ?? nextPuppetId(),
        label,
        position: overrides.position ?? { x: 0, y: 0, z: 0 },
        radius: overrides.radius ?? 1.3,
        width: overrides.width ?? 2.6,
        depth: overrides.depth ?? 2.6,
        color: overrides.color ?? "#f59e0b",
        shape: overrides.shape ?? "circle",
    };
}

function mapPuppets(
    state: BoardState,
    id: string,
    fn: (p: Puppet) => Puppet,
): BoardState {
    return {
        ...state,
        puppets: state.puppets.map((p) => (p.id === id ? fn(p) : p)),
    };
}

export function boardReducer(state: BoardState, action: BoardAction): BoardState {
    switch (action.type) {
        case "setState":
            return action.state;
        case "addPuppet":
            return { ...state, puppets: [...state.puppets, action.puppet] };
        case "movePuppet":
            return mapPuppets(state, action.id, (p) => ({ ...p, position: action.position }));
        case "rotatePuppet":
            return mapPuppets(state, action.id, (p) => ({ ...p, rotationY: action.rotationY }));
        case "renamePuppet":
            return mapPuppets(state, action.id, (p) => ({ ...p, name: action.name }));
        case "setNote":
            return mapPuppets(state, action.id, (p) => ({ ...p, note: action.note }));
        case "setAction":
            return mapPuppets(state, action.id, (p) => ({ ...p, action: action.action }));
        case "setTitle":
            return { ...state, title: action.title };
        case "updatePuppetAttributes":
            return mapPuppets(state, action.id, (p) => ({
                ...p,
                face: action.face ?? p.face,
                complexity: action.complexity ?? p.complexity,
                clothes: action.clothes ?? p.clothes,
            }));
        case "removePuppet":
            return {
                ...state,
                puppets: state.puppets.filter((p) => p.id !== action.id),
                // drop any links touching the removed figure
                links: (state.links ?? []).filter(
                    (l) => l.source !== action.id && l.target !== action.id,
                ),
            };
        case "addNote":
            return { ...state, notes: [...(state.notes ?? []), action.note] };
        case "moveNote":
            return {
                ...state,
                notes: (state.notes ?? []).map((n) =>
                    n.id === action.id ? { ...n, position: action.position } : n,
                ),
            };
        case "editNote":
            return {
                ...state,
                notes: (state.notes ?? []).map((n) =>
                    n.id === action.id ? { ...n, text: action.text } : n,
                ),
            };
        case "removeNote":
            return { ...state, notes: (state.notes ?? []).filter((n) => n.id !== action.id) };
        case "addZone":
            return { ...state, zones: [...(state.zones ?? []), action.zone] };
        case "moveZone":
            return {
                ...state,
                zones: (state.zones ?? []).map((z) =>
                    z.id === action.id ? { ...z, position: action.position } : z,
                ),
            };
        case "editZone":
            return {
                ...state,
                zones: (state.zones ?? []).map((z) =>
                    z.id === action.id
                        ? {
                              ...z,
                              label: action.label ?? z.label,
                              color: action.color ?? z.color,
                              radius: action.radius ?? z.radius,
                              width: action.width ?? z.width,
                              depth: action.depth ?? z.depth,
                              shape: action.shape ?? z.shape,
                          }
                        : z,
                ),
            };
        case "removeZone":
            return { ...state, zones: (state.zones ?? []).filter((z) => z.id !== action.id) };
        case "setTableShape":
            return { ...state, table: { ...state.table, shape: action.shape } };
        case "setTableColor":
            return { ...state, table: { ...state.table, color: action.color } };
        case "setAmbiance":
            return { ...state, ambiance: action.ambiance };
        case "addLink":
            return { ...state, links: [...(state.links ?? []), action.link] };
        case "editLink":
            return {
                ...state,
                links: (state.links ?? []).map((l) =>
                    l.id === action.id
                        ? {
                              ...l,
                              label: action.label ?? l.label,
                              color: action.color ?? l.color,
                              weight: action.weight ?? l.weight,
                          }
                        : l,
                ),
            };
        case "removeLink":
            return { ...state, links: (state.links ?? []).filter((l) => l.id !== action.id) };
        default:
            return state;
    }
}

export const initialBoardState = emptyBoardState;

// --- figure ownership (issue #197) ----------------------------------------

/** Who is editing the board. The therapist may edit any figure; a participant
 *  may edit only the figures they own. */
export interface EditorIdentity {
    isTherapist: boolean;
    participantId: string | null;
    displayName?: string;
}

/** Whether `actor` may move/edit/delete `puppet`. Figures without ownership
 *  (legacy state schema v1) are therapist-owned, so a participant cannot edit
 *  them. */
export function canEditFigure(puppet: Puppet, actor: EditorIdentity): boolean {
    if (actor.isTherapist) return true;
    if ((puppet.ownerType ?? FigureOwnerType.THERAPIST) !== FigureOwnerType.PARTICIPANT) {
        return false;
    }
    return puppet.ownerId != null && puppet.ownerId === actor.participantId;
}

const FIGURE_MUTATIONS: ReadonlySet<BoardAction["type"]> = new Set([
    "movePuppet",
    "rotatePuppet",
    "renamePuppet",
    "setNote",
    "setAction",
    "updatePuppetAttributes",
    "removePuppet",
]);

/** Wrap the board reducer so a participant can only mutate their own figures.
 *  Unauthorized figure mutations are dropped (no-op); the therapist bypasses
 *  every guard. Newly added figures are stamped with the acting participant's
 *  ownership so the client and server agree on the owner. */
export function guardedBoardReducer(actor: EditorIdentity) {
    return (state: BoardState, action: BoardAction): BoardState => {
        if (action.type === "addPuppet" && !actor.isTherapist) {
            const owned: Puppet = {
                ...action.puppet,
                ownerId: actor.participantId,
                ownerType: FigureOwnerType.PARTICIPANT,
                ownerName: actor.displayName ?? action.puppet.ownerName ?? "",
            };
            return boardReducer(state, { ...action, puppet: owned });
        }
        if (FIGURE_MUTATIONS.has(action.type)) {
            const id = (action as { id: string }).id;
            const target = state.puppets.find((p) => p.id === id);
            if (target && !canEditFigure(target, actor)) {
                return state; // not the owner — drop the mutation
            }
        }
        return boardReducer(state, action);
    };
}
